import { type ChallengeConfig } from '../../challenge-configs-api.ts';
import { type CourseListItem } from '../../courses-api.ts';
import { type PerformanceDashboard, type PerformanceDashboardParams } from '../performance-api.ts';
import { buildGames } from './games.ts';
import { buildMlAgreement } from './ml-agreement.ts';
import { buildPartners } from './partners.ts';
import { type PeriodRange } from './period-range.ts';
import { buildReviewQueue } from './review-queue.ts';
import { filterSessionsInScope, isCompleted } from './sessions.ts';
import { type SessionsServiceSession } from './sessions-service-api.ts';
import { buildStuckSessions } from './stuck-sessions.ts';
import { buildTopPlayers } from './top-players.ts';
import { buildTopWins } from './top-wins.ts';
import { sumTotals } from './totals.ts';
import { buildTrend } from './trend.ts';

export type PerformanceSources = {
  params: PerformanceDashboardParams;
  range: PeriodRange;
  now: Date;
  sessions: SessionsServiceSession[];
  courses: CourseListItem[];
  challengeConfigs: ChallengeConfig[];
  regionCourses: CourseListItem[] | undefined;
};

export const buildPerformanceDashboard = ({
  params,
  range,
  now,
  sessions,
  courses,
  challengeConfigs,
  regionCourses,
}: PerformanceSources): PerformanceDashboard => {
  const inScope = filterSessionsInScope(sessions, params, courses, regionCourses);
  const completed = inScope.filter(isCompleted);
  const topPlayers = buildTopPlayers(completed);
  const configsById = new Map(challengeConfigs.map((config) => [config.id, config]));

  return {
    period: { from: range.from.toISOString(), to: range.to.toISOString(), bucket: range.bucket },
    kpis: { ...sumTotals(completed), players: topPlayers.unique },
    trend: buildTrend(completed, range, params.time_zone),
    partners: buildPartners(completed, courses),
    games: buildGames(completed, configsById),
    stuck_sessions: buildStuckSessions(inScope, configsById, now),
    review_queue: buildReviewQueue(completed, now),
    ml_agreement: buildMlAgreement(completed),
    top_players: topPlayers,
    top_wins: buildTopWins(completed, configsById),
  };
};
